import { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useGetOne, useRefresh, useUpdate } from "react-admin";
import { ClaimItem, Claimant, ResourceName } from "@/types";
import { featureFlags } from "@/utils/featureFlags";
import { getEffectiveDeductible, getResolvedClaimsValues } from "@/resources/claimants/hooks/useCurrentPolicyData";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { describeIneligibleReason } from "../ineligibleReasons";

/**
 * Reason copy lives in ../ineligibleReasons.ts (one list for the banner, the list
 * facet and the load notice, #7380); re-exported so existing importers keep working.
 */
export { INELIGIBLE_REASON_LABELS, describeIneligibleReason } from "../ineligibleReasons";

interface EligibilityBannerProps {
  record: ClaimItem | undefined;
  canEdit: boolean;
}

/**
 * Banner shown on an ineligible claim line.
 *
 * Flag off (legacy): the line's eligibility is a stored flag the user may flip, so the
 * banner offers "Mark Eligible" once the claimant has met the deductible (with an
 * override path below it).
 *
 * Flag on (ADR-081 D5): the stamp follows the deductible basis on every load and edit
 * and cannot be set by hand, so the action and its dialog are gone; the banner names
 * each reason in plain language and says what would change it.
 */
export const EligibilityBanner = ({ record, canEdit }: EligibilityBannerProps) => {
  const basisDriven = featureFlags.deductibleBasisViewEnabled;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [update] = useUpdate();
  const refresh = useRefresh();

  // Fetch claimant to check deductible status
  const { data: claimant } = useGetOne<Claimant>(
    ResourceName.CLAIMANTS,
    { id: record?.claimant_id_ref || "" },
    { enabled: !!record?.claimant_id_ref }
  );

  // Fetch policy to get deductible amount
  const { data: policy } = useGetOne(
    ResourceName.POLICIES,
    { id: record?.policy_id_ref || "" },
    { enabled: !!record?.policy_id_ref }
  );

  // Check if claimant is over deductible for this policy
  const policyId = record?.policy_id_ref;
  const policyData = policyId && claimant?.policy_data?.[policyId];
  // Authoritative total: blended for a carve-out claimant, and the correct
  // authority-selected ledger for bundled (was reading .calculated only —
  // ADR-077 / #5731 review).
  const totalPaid = getResolvedClaimsValues(policyData || undefined)?.total_paid ?? 0;
  // Effective specific deductible after laser precedence (exclusion 0 > contract
  // laser > straight laser > base). Gating on the base deductible falsely showed
  // "Has Met Deductible" for a lasered claimant under their higher effective
  // deductible (Bug #5879). One denominator drives both the gate and all copy.
  const { amount: deductible } = getEffectiveDeductible(policyData || undefined, policy);
  const isOverDeductible = deductible > 0 && totalPaid >= deductible;
  const deductiblePercentage = deductible > 0 ? Math.round((totalPaid / deductible) * 100) : 0;
  const reasons = record?.ineligible_reasons ?? [];

  const handleMarkEligible = async () => {
    if (!record) return;
    await update(ResourceName.CLAIM_ITEMS, {
      id: record.id,
      data: { is_eligible: true, ineligible_reasons: [] },
      previousData: record,
    });
    setConfirmOpen(false);
    refresh();
  };

  if (basisDriven) {
    return (
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2, alignItems: "center" }} data-testid="eligibility-banner">
        <AlertTitle>Not Eligible for Stop-Loss</AlertTitle>
        Eligibility follows the specific benefit's incurred and paid windows (the policy term is only a fallback). It is
        recomputed on every load and edit and cannot be set by hand.
        {reasons.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
            {reasons.map((r) => (
              <Typography key={r} component="li" variant="caption" display="list-item">
                {describeIneligibleReason(r)}
              </Typography>
            ))}
          </Box>
        )}
        {/* Progress is read from the STAMPED term, so it only means something when the line
            counts there and is simply below the deductible (!4855 thread 29897). The stamp
            calls the claimant "over" only when the resolved overage is strictly positive, so
            when this page's own read already shows the deductible met the line would
            contradict the reason; hide it rather than argue (thread 29974). */}
        {deductible > 0 && reasons.includes("below_deductible") && !isOverDeductible && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
            Claimant has paid {formatUSD(totalPaid)} of {formatUSD(deductible)} deductible ({deductiblePercentage}%).
          </Typography>
        )}
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
          To change it, correct the line's dates or status, or the benefit's windows and contract basis. The stamp
          follows on the next recompute.
        </Typography>
      </Alert>
    );
  }

  return (
    <>
      <Alert
        severity="info"
        icon={<InfoIcon />}
        sx={{ mb: 2, alignItems: "center" }}
        data-testid="eligibility-banner"
        action={
          canEdit && isOverDeductible ? (
            <MuiButton size="small" onClick={() => setConfirmOpen(true)} sx={{ whiteSpace: "nowrap", mr: 1 }}>
              Mark Eligible
            </MuiButton>
          ) : undefined
        }
      >
        <AlertTitle>Not Eligible for Stop-Loss</AlertTitle>
        This claim item is marked as ineligible.
        {reasons.length > 0 && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Reasons: {reasons.map((r) => formatToTitleCase(r.replace(/_/g, " "))).join(", ")}
          </Typography>
        )}
        {canEdit && !isOverDeductible && deductible > 0 && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
            Claimant has paid {formatUSD(totalPaid)} of {formatUSD(deductible)} deductible ({deductiblePercentage}%).
            Must meet deductible before marking eligible.
          </Typography>
        )}
      </Alert>

      {/* Confirmation dialog with deductible check */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Eligible</DialogTitle>
        <DialogContent>
          {!isOverDeductible ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Claimant Has Not Met Deductible</AlertTitle>
              <Typography variant="body2">
                {claimant?.claimant_name || "This claimant"} has paid <strong>{formatUSD(totalPaid)}</strong> of the{" "}
                <strong>{formatUSD(deductible)}</strong> specific deductible ({deductiblePercentage}%).
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Marking this item as eligible before the claimant meets their deductible may result in incorrect
                stop-loss calculations.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Claimant Has Met Deductible</AlertTitle>
              <Typography variant="body2">
                {claimant?.claimant_name || "This claimant"} has paid <strong>{formatUSD(totalPaid)}</strong> of the{" "}
                <strong>{formatUSD(deductible)}</strong> specific deductible ({deductiblePercentage}%).
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            This will mark the claim item as eligible and clear all ineligible reasons. Are you sure?
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmOpen(false)}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            onClick={handleMarkEligible}
            color={isOverDeductible ? "primary" : "warning"}
            disableElevation
          >
            {isOverDeductible ? "Mark Eligible" : "Override & Mark Eligible"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
